import React, { useEffect } from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import styles from './styles.sass'
import ContentLoading from 'components/ContentLoading'
import ContentError from 'components/ContentError'
import Login from './Login'

import WorkspaceSignupContainer from './WorkspaceSignupContainer'
import WorkspaceSelector from './WorkspaceSelector'
import SessionStore from 'iam/SessionStore'
import AuthenticationContainerState from './AuthenticationContainerState'
import { useBrandingSettings } from 'hooks/branding'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  authenticationState: AuthenticationContainerState
  inviteToken?: string
  sessionStore: SessionStore
  children: React.ReactNode
}

export default observer(function AuthenticationContainer({
  authenticationState,
  inviteToken,
  sessionStore,
  children,
}: Props) {
  // Fetches the current user and WS once we are authenticated.
  useEffect(() => {
    authenticationState.activate(inviteToken)
  }, [inviteToken])

  const { landingPageImage } = useBrandingSettings()

  const displayState = () => {
    if (!authenticationState.isAuthenticated) {
      return 'authentication'
    } else if (authenticationState.workspaceSignupState.isConfigurerOpen) {
      return 'configuration'
    } else {
      return authenticationState.workspaceSelectorState.isSelectorOpen
        ? 'portal'
        : 'portalClosed'
    }
  }

  const display = displayState()

  return (
    <div className={cx(styles.container, styles[display])}>
      {authenticationState.activate.match({
        pending: () => <ContentLoading />,
        rejected: (err: any) => <ContentError error={err} />,
        resolved: () => (
          <>
            {display === 'authentication' && (
              <Login controller={authenticationState} />
            )}
            <div className={styles.image}>
              <img src={landingPageImage} />
            </div>
            <WorkspaceSignupContainer
              workspaceSignupState={authenticationState.workspaceSignupState}
            />
            <WorkspaceSelector
              selectorState={authenticationState.workspaceSelectorState}
              workspaceSignupState={authenticationState.workspaceSignupState}
              sessionStore={sessionStore}
            />
            <div className={styles.navigation}>{children}</div>
          </>
        ),
      })}
    </div>
  )
})
